import { ExpandMore } from '@tunasong/icons'
import type { PerformancePart } from '@tunasong/schemas'
import {
  partsLabel,
  partsList,
  performancePartLabels,
  performanceParts as allParts,
  togglePart,
} from '@tunasong/schemas'
import {
  Button,
  Menu,
  MenuCheckboxItem,
  MenuContent,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@tunasong/ui-lib'
import { useId, type FC } from 'react'
import type { PartsScope, PerformanceParts } from './performance-parts.hook.js'
import styles from './part-prompts.module.css'

export interface PartMenuProps {
  performanceParts: PerformanceParts
  /** Controlled open state, so "Change for this band" and the R shortcut can open the menu. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function isPartsScope(value: unknown): value is PartsScope {
  return value === 'band' || value === 'song'
}

/**
 * Header "Playing Vocals + Guitar ▾": the parts this person plays. Each part is a checkbox item, so several
 * can be set in one visit. "Apply to" picks where a change goes: every song in the band (every own song,
 * outside a band), or this song only. Choosing "This song only" saves the current parts for the song at
 * once (D13); "Use band parts again" deletes that override.
 */
export const PartMenu: FC<PartMenuProps> = ({ performanceParts, open, onOpenChange }) => {
  const { resolution, parts, applyTo, chooseParts, setApplyTo, restoreBandParts } = performanceParts
  const band = resolution.status === 'resolving' ? null : resolution.band
  const isSongOverride = applyTo === 'song'
  const hintId = useId()

  const handleToggle = (part: PerformancePart) => () => chooseParts(togglePart(parts, part))
  const handleApplyTo = (value: unknown) => {
    if (isPartsScope(value)) {
      setApplyTo(value)
    }
  }

  return (
    <Menu open={open} onOpenChange={onOpenChange}>
      <MenuTrigger
        render={
          <Button
            variant="text"
            color="inherit"
            endIcon={<ExpandMore />}
            className={styles.trigger}
            aria-label={`Playing ${partsList(parts)}${isSongOverride ? ', this song only' : ''}`}
          >
            {/* With the scope marker, "Playing" gives way on narrower screens so the trigger stays on one line */}
            <span className={isSongOverride ? styles.playingWordOptional : undefined}>Playing </span>
            {partsLabel(parts)}
            {isSongOverride ? ' · this song' : null}
          </Button>
        }
      />
      <MenuContent>
        <MenuGroup aria-describedby={hintId}>
          <MenuGroupLabel>{band ? `Your parts in ${band.name}` : 'Your parts'}</MenuGroupLabel>
          <div id={hintId} className={styles.menuHint}>
            Pick all that apply
          </div>
          {allParts.map(part => (
            <MenuCheckboxItem
              key={part}
              className={styles.menuItem}
              checked={parts.includes(part)}
              onCheckedChange={handleToggle(part)}
            >
              {performancePartLabels[part]}
            </MenuCheckboxItem>
          ))}
        </MenuGroup>
        <MenuSeparator />
        <MenuRadioGroup value={applyTo} onValueChange={handleApplyTo}>
          <MenuGroupLabel>Apply to</MenuGroupLabel>
          <MenuRadioItem value="band" className={styles.menuItem} closeOnClick={false}>
            {band ? `All songs in ${band.name}` : 'All my songs'}
          </MenuRadioItem>
          <MenuRadioItem value="song" className={styles.menuItem} closeOnClick={false}>
            This song only
          </MenuRadioItem>
        </MenuRadioGroup>
        {isSongOverride && band ? (
          <>
            <MenuSeparator />
            <MenuItem className={styles.menuItem} onClick={restoreBandParts}>
              Use band parts again
            </MenuItem>
          </>
        ) : null}
      </MenuContent>
    </Menu>
  )
}

export default PartMenu
